import _ from 'lodash';
import { produce } from 'immer';
import React, { FC } from 'react';
import { useDispatch } from 'react-redux';

import { Archive } from '../shared/rowActions/Archive';
import { Edit } from '../shared/rowActions/Edit';
import { isHierarchicalItem } from 'src/lib/hierarchy';
import { nameWithAncestry } from './hierarchy';
import { RowActionContainer, StyledTD, StyledTR } from 'src/presentation/components/pages/shared/Table';
import { TD } from '@ad-scripts/presentation/components/common/table';
import RouterLinkButton from 'src/presentation/components/RouterLinkButton';
import SubTopicConnector from '@ad-scripts/presentation/components/reporting/report/lib/sub_topic_connector';
import TablePage, { TablePageRender } from '../shared/TablePage';
import theme from '@ad-scripts/presentation/themes/default';
import TopicEditor from './TopicEditor';
import topicsResource, { HierarchicalTopic, Topic } from 'src/actions/topics/topicsResource';
import useIsFeatureEnabled from 'src/presentation/hooks/useIsFeatureEnabled';
import useTopics from 'src/presentation/hooks/useTopics';

const dataAidPrefix = 'admin-topics';
const columnConfig = [{ attr: 'name', label: 'Name' }];
const tabConfig = [
  { label: 'Topics', filter: (topic: Topic) => !topic.disabled, key: `${dataAidPrefix}-tab-enabled` },
  { label: 'Archived', filter: (topic: Topic) => topic.disabled, key: `${dataAidPrefix}-tab-disabled` },
];

const Topics: TablePageRender = ({ root }) => {
  const { errors, dataMergeFinished, values } = useTopics();
  const actions = (
    <RouterLinkButton data-aid={`${dataAidPrefix}-addNew`} to={`${root}/new`}>
      Create Topic
    </RouterLinkButton>
  );
  const isFeatureEnabled = useIsFeatureEnabled();
  const customAttributesPageInfo = !isFeatureEnabled('customAttributes') ? null : (
    <>
      {' '}
      If you&apos;d like to include topics that are filled in by your agents,{' '}
      <a
        target="_blank"
        rel="noopener noreferrer"
        href={`/admin/freeform-topics`}
        onClick={evt => {
          evt.preventDefault();
          window.location.assign('/admin/freeform-topics');
        }}
      >
        create Freeform Topics here.
      </a>
    </>
  );

  const description = (
    <span>
      Topics help you spot trends in conversations. Apply them to conversations and run reports to learn the most common
      reasons your customers reach out. Read our{' '}
      <a
        target="_blank"
        rel="noopener noreferrer"
        href="https://help.gladly.com/docs/principles-of-hierarchical-topics/"
      >
        guide to creating topics{' '}
      </a>
      that get you the best insights.
      {customAttributesPageInfo}
    </span>
  );

  return (
    <TablePage
      actions={actions}
      columnConfig={columnConfig}
      data-aid={`${dataAidPrefix}-page`}
      description={description}
      editor={TopicEditor}
      errors={errors}
      filterFunc={customFilter}
      isLoading={!dataMergeFinished}
      items={values}
      name="Topics"
      renderRow={renderRow}
      resetErrors={topicsResource.actions.resetErrors}
      root={root}
      tabConfig={tabConfig}
    />
  );
};

export function renderRow(topic: NestedTopic) {
  return (
    <TopicHierarchyRow
      columnConfig={columnConfig}
      dataAid={dataAidPrefix}
      depth={topic.depth}
      item={topic}
      key={topic.id}
    />
  );
}

export function customFilter(allTopics: HierarchicalTopic[], searchString: string, tab: 'Archived' | 'Topics') {
  const tabFilteredTopics = tabFilter(allTopics, tab);
  const searchFilteredTopics = searchString ? searchFilter(tabFilteredTopics, searchString) : tabFilteredTopics;
  const topics: NestedTopic[] = _.map(searchFilteredTopics, topic => ({
    ...topic,
    depth: 0,
    name: topic.id === '' ? 'No Topic' : topic.name,
  }));

  _.forEach(topics, item => descendantsWithDepth(topics, item.id, 0));

  return topics;
}

// Helper Functions

function tabFilter(allItems: HierarchicalTopic[], selectedTab: 'Archived' | 'Topics'): HierarchicalTopic[] {
  return _.filter(allItems, item => {
    const isTopicDisabled = item.disabled;
    return selectedTab === 'Archived' ? isTopicDisabled : !isTopicDisabled;
  });
}

function searchFilter(topics: HierarchicalTopic[], searchString: string) {
  return _.filter(topics, topic =>
    topic.ancestralNames ? topic.ancestralNames.some(includesSearchString) : includesSearchString(topic.name)
  );

  function includesSearchString(receiver: string) {
    return receiver.toLowerCase().includes(searchString.toLowerCase());
  }
}

function descendantsWithDepth(items: NestedTopic[], topicId: string, depth: number) {
  const itemIndex = _.findIndex(items, ['id', topicId]);

  if (itemIndex >= 0 && items[itemIndex].depth < depth) {
    items[itemIndex].depth = depth;
  }

  if (itemIndex >= 0 && items[itemIndex].children?.length) {
    _.forEach(items[itemIndex].children, id => descendantsWithDepth(items, id, depth + 1));
  }
}

type NestedTopic = HierarchicalTopic & {
  depth: number;
};

// Custom row to display topic hierarchy in TableList

type TopicHierarchyRowProps = {
  columnConfig: { attr: string; label: string }[];
  dataAid: string;
  depth: number;
  item: Topic;
};

const TopicHierarchyRow: FC<TopicHierarchyRowProps> = props => {
  const { columnConfig, dataAid, item, depth } = props;
  const dispatch = useDispatch();

  if (!isHierarchicalItem(item)) {
    // do not render until the hierarchy is updated
    return null;
  }

  const style = { marginLeft: `calc(${depth - 1} * ${theme.fontSize.base} + ${theme.spacing.medium})` };
  const cells = _.map(columnConfig, ({ attr }) => (
    <StyledTD data-aid={`${dataAid}row-${item.id}-${attr}`} key={attr}>
      {depth > 0 ? <SubTopicConnector style={style} /> : null}
      {depth > 0 ? item.name : nameWithAncestry(item)}
    </StyledTD>
  ));

  const archiveTopic = () => {
    const toast = `We archived ${item.name}. Your topics list just got a little lighter.`;

    const updatedItem = produce(item, draft => {
      draft.disabled = true;
    });

    dispatch(
      topicsResource.effects.update({ id: item.id }, updatedItem, { customToastMsg: toast, disableWindowBack: true })
    );
  };

  return (
    <StyledTR data-aid={`${dataAid}row-${item.id}`} key={item.id}>
      {cells}
      <TD style={{ width: '1%' }}>
        <RowActionContainer>
          {!item.disabled ? (
            <Archive
              confirmationText={`Archive ${item.name} ?`}
              description={
                <>
                  Archiving this topic will also archive its child topics. Your reporting data
                  <br /> will not be affected, and you can still run reports with this topic.
                </>
              }
              submitText="Archive Topic"
              itemId={item.id}
              onArchive={archiveTopic}
            />
          ) : null}
          <Edit dataAid={`editLink-${item.id}`} id={item.id} />
        </RowActionContainer>
      </TD>
    </StyledTR>
  );
};

export default Topics;
